/**
 * Registry-driven trigger volume handler system.
 *
 * Games register handlers by `kind` string (e.g. `'radio'`, `'door'`,
 * `'jumpscare'`). The engine (or game runtime) calls `dispatchTrigger` when the
 * player enters a `MapTriggerVolume` whose kind has a registered handler.
 *
 * This module is game-agnostic - it holds no game state or references.
 */

import type { MapTriggerVolume } from '../schema/map';

/** Context provided to a trigger handler when the player enters a volume. */
export interface TriggerContext {
  /** World X of the player at the moment of trigger entry. */
  playerX: number;
  /** World Z of the player at the moment of trigger entry. */
  playerZ: number;
  /** Current floor index of the player. */
  playerFloor: number;
}

/**
 * A function that handles a player entering a specific kind of trigger volume.
 *
 * @param volume - The trigger volume definition from the map document.
 * @param ctx    - Runtime context (player position, floor, etc.).
 */
export type TriggerHandler = (volume: MapTriggerVolume, ctx: TriggerContext) => void;

const handlers = new Map<string, TriggerHandler>();

/**
 * Register a handler for a trigger volume kind.
 * If a handler for `kind` is already registered it will be replaced; this
 * allows games to override engine default handlers.
 */
export function registerTriggerHandler(kind: string, handler: TriggerHandler): void {
  handlers.set(kind, handler);
}

/**
 * Remove the handler for `kind`. No-op if no handler was registered.
 */
export function unregisterTriggerHandler(kind: string): void {
  handlers.delete(kind);
}

/**
 * Returns the handler registered for `kind`, or `null` if none exists.
 */
export function getTriggerHandler(kind: string): TriggerHandler | null {
  return handlers.get(kind) ?? null;
}

/**
 * Returns `true` if a handler has been registered for `kind`.
 */
export function hasTriggerHandler(kind: string): boolean {
  return handlers.has(kind);
}

/**
 * Dispatch a trigger volume entry event. Calls the registered handler for
 * `volume.kind` if one exists; silently no-ops otherwise.
 *
 * @returns `true` if a handler was found and called, `false` otherwise.
 */
export function dispatchTrigger(volume: MapTriggerVolume, ctx: TriggerContext): boolean {
  const handler = handlers.get(volume.kind);
  if (!handler) return false;
  handler(volume, ctx);
  return true;
}

/**
 * Remove all registered handlers. Primarily useful in unit tests to reset state
 * between test runs.
 */
export function clearTriggerHandlers(): void {
  handlers.clear();
}
